import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PERMISSIONS } from './models/authTypes';
import PermissionsManager from './pages/admin/PermissionsManager';
import UsersManager from './pages/admin/UsersManager';
import Layout from './components/Layout';
import Home from './pages/Home';
import LigasList from './pages/ligas/LigasList';
import LigaDetail from './pages/ligas/LigaDetail';
import LastresLigaPage from './pages/ligas/LastresLigaPage';
import LastresListPage from './pages/ligas/LastresListPage';
import CarrerasList from './pages/carreras/CarrerasList';
import CarreraDetail from './pages/carreras/CarreraDetail';
import SociosList from './pages/socios/SociosList';
import SocioDetail from './pages/socios/SocioDetail';
import SocioProfile from './pages/socios/SocioProfile';
import SociosImport from './pages/socios/SociosImport';
import FixSociosLiga from './pages/socios/FixSociosLiga';
import PuntuacionesList from './pages/puntuaciones/PuntuacionesList';
import PuntuacionDetail from './pages/puntuaciones/PuntuacionDetail';
import SancionesList from './pages/sanciones/SancionesList';
import SancionDetail from './pages/sanciones/SancionDetail';
import ReglasSancionesAutomaticas from './pages/sanciones/ReglasSancionesAutomaticas';
import ClasificacionesList from './pages/clasificaciones/ClasificacionesList';
import ClasificacionDetail from './pages/clasificaciones/ClasificacionDetail';
import ClasificacionEquipos from './pages/clasificaciones/ClasificacionEquipos'; 
import ClasificacionGamerPage from './pages/clasificaciones/ClasificacionGamerPage'; 
// Nuevos componentes
import ApelacionesList from './pages/apelaciones/ApelacionesList';
import ApelacionDetail from './pages/apelaciones/ApelacionDetail';
import EventosCalendar from './pages/eventos/EventosCalendar';
import EventoDetail from './pages/eventos/EventoDetail';
// Componentes de reclamaciones
import ReclamacionesList from './pages/reclamaciones/ReclamacionesList';
import ReclamacionDetail from './pages/reclamaciones/ReclamacionDetail';
// Componentes de autenticación
import Login from './pages/auth/Login';
import EquiposList from './pages/equipos/EquiposList';
import EquipoDetail from './pages/equipos/EquipoDetail';
import GuiaBienvenida from './pages/guia/GuiaBienvenida';
import GeneradorMiniaturas from './pages/miniaturas/GeneradorMiniaturas';
import Blog from './pages/blog/Blog';

// Componente para proteger rutas
const ProtectedRoute = ({ children, requiredPermission }: { children: React.ReactNode, requiredPermission?: string }) => {
  const { currentUser, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) return <div>Cargando...</div>;
  if (!currentUser) return <Navigate to="/login" state={{ from: location }} replace />;
  
  // Si el usuario es superadmin, siempre tiene acceso
  if (currentUser.role === 'superadmin') return <>{children}</>;
  
  // Para otros usuarios, verificar el permiso requerido si lo hay
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <div>No tienes permiso para acceder a esta página.</div>;
  }

  return <>{children}</>;
};

// Componente para la página de login
const LoginPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  if (currentUser) {
    return <Navigate to={from} replace />;
  }

  return <Login />;
};

// Componente principal de la aplicación
function App() { 

  // Import dinámico para evitar error de import arriba
  const RedaccionIACircuitos = React.lazy(() => import('./pages/ia/RedaccionIACircuitos'));

  // Importación para WarmUp
  const WarmUpPage = React.lazy(() => import('./pages/warmup/WarmUpPage'));

  return (
    <AppProvider>
      <AuthProvider>
        <Router>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
          <Routes>
            {/* Ruta de login */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Ruta raíz redirige a /home */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* Ruta pública para WarmUp */}
            <Route path="/warmup" element={
              <React.Suspense fallback={<div>Cargando WarmUp...</div>}>
                <WarmUpPage />
              </React.Suspense>
            } />
            
            {/* Layout principal para rutas protegidas */}
            <Route element={
              <ProtectedRoute>
                <Layout>
                  <Outlet />
                </Layout>
              </ProtectedRoute>
            }>
              {/* Rutas dentro del layout */}
              <Route path="home" element={<Home />} />
              {/* Redacción IA de Circuitos */}
              <Route path="ia/redaccion-circuitos" element={
                <React.Suspense fallback={<div>Cargando IA...</div>}>
                  <RedaccionIACircuitos />
                </React.Suspense>
              } />
              
              {/* Blog Page */}
              <Route path="blog" element={<Blog />} />
              
              {/* Rutas para Ligas */}
              <Route path="ligas">
                <Route index element={<LigasList />} />
                <Route path=":id" element={<LigaDetail />} />
                <Route path=":id/lastres" element={<LastresLigaPage />} />
              </Route>
              <Route path="lastres" element={<LastresListPage />} />
                    
                    {/* Rutas para Carreras */}
                    <Route path="carreras" element={<CarrerasList />} />
                    <Route path="carreras/:id" element={<CarreraDetail />} />
                    
                    {/* Rutas para Socios */}
                    <Route path="socios" element={<SociosList />} />
                    <Route path="socios/import" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_PILOTOS}>
                        <SociosImport />
                      </ProtectedRoute>
                    } />
                    <Route path="socios/:id" element={<SocioDetail />} />
                    <Route path="socios/profile/:id" element={<SocioProfile />} />
                    
                    {/* Ruta temporal para corregir socios de liga */}
                    <Route path="fix-socios-liga" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_PILOTOS}>
                        <FixSociosLiga />
                      </ProtectedRoute>
                    } />
                    
                    {/* Rutas para Clasificaciones */}
                    <Route path="clasificaciones" element={<ClasificacionesList />} />
                    <Route path="clasificaciones/:id" element={<ClasificacionDetail />} />
                    <Route path="clasificaciones/equipos/:id" element={<ClasificacionEquipos />} />
                    <Route path="clasificacion-gamer/:ligaId" element={<ClasificacionGamerPage />} />
                    
                    {/* Rutas para Puntuaciones */}
                    <Route path="puntuaciones" element={<PuntuacionesList />} />
                    <Route path="puntuaciones/:id" element={<PuntuacionDetail />} />
                    
                    {/* Rutas para Equipos */}
                    <Route path="equipos" element={<EquiposList />} />
                    <Route path="equipos/:id" element={<EquipoDetail />} />
                    <Route path="equipos/new" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_EQUIPOS}>
                        <EquipoDetail />
                      </ProtectedRoute>
                    } />
                    
                    {/* Guía de Bienvenida */}
                    <Route path="guia-bienvenida" element={<GuiaBienvenida />} />
                    
                    {/* Rutas para Sanciones */}
                    <Route path="sanciones" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_SANCIONES}>
                        <SancionesList />
                      </ProtectedRoute>
                    } />
                    <Route path="sanciones/:id" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_SANCIONES}>
                        <SancionDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="sanciones/reglas-automaticas" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_SANCIONES}>
                        <ReglasSancionesAutomaticas />
                      </ProtectedRoute>
                    } />
                    
                    {/* Rutas para Apelaciones */}
                    <Route path="apelaciones" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_APELACIONES}>
                        <ApelacionesList />
                      </ProtectedRoute>
                    } />
                    <Route path="apelaciones/new" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_APELACIONES}>
                        <ApelacionDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="apelaciones/:id" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_APELACIONES}>
                        <ApelacionDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="apelaciones/:id/resolver" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_APELACIONES}>
                        <ApelacionDetail />
                      </ProtectedRoute>
                    } />
                    
                    {/* Rutas para Eventos Comunitarios */}
                    <Route path="eventos" element={<EventosCalendar />} />
                    <Route path="eventos/:id" element={<EventoDetail />} />
                    
                    {/* Rutas para Reclamaciones */}
                    <Route path="reclamaciones" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_RECLAMACIONES}>
                        <ReclamacionesList />
                      </ProtectedRoute>
                    } />
                    <Route path="reclamaciones/new" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_RECLAMACIONES}>
                        <ReclamacionDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="reclamaciones/:id" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_RECLAMACIONES}>
                        <ReclamacionDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="miniaturas/generador-miniaturas" element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_GENERADOR_MINIATURAS}>
                        <GeneradorMiniaturas />
                      </ProtectedRoute>
                    } />
                    
              {/* Rutas de administración */}
              <Route path="/admin/roles" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADMIN_PERMISOS}>
                  <PermissionsManager />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/usuarios" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADMIN_USUARIOS}>
                  <UsersManager />
                </ProtectedRoute>
              } />
              
              {/* Ruta comodín para cualquier ruta no encontrada */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </AppProvider>
  );
}

export default App;